import { createContext, useCallback, useContext } from 'react'

import { redirectToUrl } from '../utils/urls'

/**
 * Navigation capabilities a host app can hand to the app-shell.
 *
 * Publish renders the shell inside its React Router tree and provides this
 * context from `useNavigate` / `useLocation`, so shell banners and modals that
 * link into Publish navigate client-side. Satellite apps (Account, Analyze,
 * Start Page) load the shell as a remote bundle without a provider, and every
 * navigation there falls back to a full-page load.
 */
export type HostNavigation = {
  /** Navigates the host router to a same-origin path (pathname + search + hash). */
  navigate: (path: string) => void
  /** The host router's current pathname. Changes on client-side navigation. */
  pathname: string
}

const HostNavigationContext = createContext<HostNavigation | null>(null)

export const HostNavigationProvider = HostNavigationContext.Provider

/**
 * The host app's navigation, or `null` when no host provides one (satellites,
 * the standalone test app).
 */
export function useHostNavigation(): HostNavigation | null {
  return useContext(HostNavigationContext)
}

/**
 * Navigate to a URL. Same-origin targets go through the host router when one
 * is provided; everything else (cross-origin, or no host) is a full-page load.
 *
 * @example
 * ```tsx
 * const navigate = useHostNavigate()
 * navigate(getPublishUrl('settings/channels'))
 * ```
 */
export function useHostNavigate(): (href: string) => void {
  const hostNavigate = useHostNavigation()?.navigate

  return useCallback(
    (href: string): void => {
      const url = new URL(href, window.location.origin)

      if (hostNavigate && url.origin === window.location.origin) {
        hostNavigate(`${url.pathname}${url.search}${url.hash}`)
        return
      }

      redirectToUrl(href)
    },
    [hostNavigate],
  )
}
